import { z } from 'zod';
import { paginationQuerySchema } from './pagination';

export const CAMPAIGN_STATUSES = ['DRAFT', 'PENDING_REVIEW', 'SUBMITTED', 'ACTIVE', 'PAUSED', 'FINISHED', 'REJECTED'] as const;
export type CampaignStatusKey = (typeof CAMPAIGN_STATUSES)[number];
export const AD_DESTINATIONS = ['STOREFRONT', 'WHATSAPP'] as const;
export type AdDestinationKey = (typeof AD_DESTINATIONS)[number];

/** Objetivo do modelo de campanha conforme o destino (14.3). */
export const DESTINATION_OBJECTIVE: Record<AdDestinationKey, 'SALES' | 'MESSAGES'> = {
  STOREFRONT: 'SALES',
  WHATSAPP: 'MESSAGES',
};

export const AD_SEGMENTS = ['moda', 'beleza', 'alimentacao', 'servicos', 'outros'] as const;
export type AdSegment = (typeof AD_SEGMENTS)[number];

export const MAX_CAMPAIGN_PRODUCTS = 5;
/** Menor verba diária aceita (a Meta recusa conjuntos com verba muito baixa). */
export const MIN_DAILY_BUDGET_CENTS = 600;
/** Campanha sem data de fim exige saldo para 7 dias de verba (14.3). */
export const OPEN_ENDED_RESERVE_DAYS = 7;
/** Saldo abaixo de 2 dias de verba das campanhas ativas → aviso (14.5). */
export const LOW_BALANCE_DAYS = 2;

/** Por que a campanha está pausada. */
export const CAMPAIGN_PAUSE_REASONS = ['MERCHANT', 'NO_CREDITS', 'MODULE_DISABLED'] as const;
export type CampaignPauseReason = (typeof CAMPAIGN_PAUSE_REASONS)[number];

// ---------- Público (14.3) ----------
const idName = z.object({ id: z.string().trim().min(1).max(60), name: z.string().trim().min(1).max(120) });

export const adLocationSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('cities'), cities: z.array(idName).min(1).max(25) }),
  z.object({
    kind: z.literal('radius'),
    /** Endereço de referência (normalmente o do negócio). */
    address: z.string().trim().min(5).max(200),
    radiusKm: z.number().int().min(1).max(80),
  }),
]);

export const targetingSchema = z
  .object({
    ageMin: z.number().int().min(18).max(65).default(18),
    ageMax: z.number().int().min(18).max(65).default(65),
    genders: z.enum(['all', 'female', 'male']).default('all'),
    interests: z.array(idName).max(10).default([]),
    location: adLocationSchema,
  })
  .refine((t) => t.ageMin <= t.ageMax, { message: 'A idade mínima não pode passar da máxima.', path: ['ageMax'] });
export type Targeting = z.infer<typeof targetingSchema>;

// ---------- Conexão (14.2) ----------
export const adsPagesSchema = z.object({ accessToken: z.string().trim().min(10).max(1000) });
export const connectAdsSchema = z.object({
  /** Token do Login com Facebook (escopos de página). Usado só nesta chamada. */
  accessToken: z.string().trim().min(10).max(1000),
  pageId: z.string().trim().regex(/^\d{5,30}$/, 'Página inválida.'),
  instagramId: z.string().trim().regex(/^\d{5,30}$/).nullable().optional(),
});
export type ConnectAdsInput = z.infer<typeof connectAdsSchema>;

// ---------- Assistente (14.3) ----------
export const adsSearchQuerySchema = z.object({ q: z.string().trim().min(2).max(60) });
export const adsSuggestionsQuerySchema = z.object({
  segment: z.enum(AD_SEGMENTS),
  destination: z.enum(AD_DESTINATIONS),
});

export const estimateReachSchema = z.object({
  targeting: targetingSchema,
  dailyBudgetCents: z.number().int().min(MIN_DAILY_BUDGET_CENTS).max(10_000_000),
});
export type EstimateReachInput = z.infer<typeof estimateReachSchema>;

export const createCampaignSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  segment: z.enum(AD_SEGMENTS),
  destination: z.enum(AD_DESTINATIONS),
  targeting: targetingSchema,
  productIds: z.array(z.string().uuid()).min(1).max(MAX_CAMPAIGN_PRODUCTS),
  dailyBudgetCents: z.number().int().min(MIN_DAILY_BUDGET_CENTS).max(10_000_000),
  /** `null` = sem data de fim. */
  durationDays: z.number().int().min(1).max(90).nullable(),
  startsAt: z.coerce.date().optional(),
});
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

const creativeEdit = z.object({
  id: z.string().uuid(),
  headline: z.string().trim().min(2).max(40),
  primaryText: z.string().trim().min(2).max(500),
});

/** Ajustes do lojista em rascunho, devolvida ou reprovada. */
export const updateCampaignSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  targeting: targetingSchema.optional(),
  dailyBudgetCents: z.number().int().min(MIN_DAILY_BUDGET_CENTS).max(10_000_000).optional(),
  durationDays: z.number().int().min(1).max(90).nullable().optional(),
  creatives: z.array(creativeEdit).max(MAX_CAMPAIGN_PRODUCTS).optional(),
});
export type UpdateCampaignInput = z.infer<typeof updateCampaignSchema>;

export const listCampaignsQuerySchema = paginationQuerySchema.extend({ status: z.enum(CAMPAIGN_STATUSES).optional() });

// ---------- Revisão pela equipe (14.4) ----------
export const reviewCampaignSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('approve'),
    creatives: z.array(creativeEdit).max(MAX_CAMPAIGN_PRODUCTS).optional(),
    targeting: targetingSchema.optional(),
  }),
  z.object({ action: z.literal('return'), comment: z.string().trim().min(5).max(1000) }),
]);
export type ReviewCampaignInput = z.infer<typeof reviewCampaignSchema>;

// ---------- Textos do modelo ----------
/** Troca `{{produto}}`, `{{preco}}`, `{{loja}}`. Variável desconhecida fica vazia. */
export function renderAdText(template: string, vars: Record<string, string>): string {
  return template
    .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) => vars[k] ?? '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/ /g, ' ');
}

/**
 * Saldo exigido para criar ou enviar a campanha: verba total, ou 7 dias de verba
 * quando não tem fim (14.3).
 */
export function requiredCreditsCents(dailyBudgetCents: number, durationDays: number | null): number {
  return dailyBudgetCents * (durationDays ?? OPEN_ENDED_RESERVE_DAYS);
}

// ---------- Relatório (14.7) ----------
export interface CampaignTotals {
  spendCents: number;
  impressions: number;
  reach: number;
  clicks: number;
  conversions: number;
  conversionValueCents: number;
}

export interface CampaignReport extends CampaignTotals {
  /** Custo por venda (ou por conversa), em centavos. */
  costPerConversionCents: number | null;
  /** Vendas ÷ gasto. */
  roas: number | null;
  comment: string;
}

export function campaignReport(t: CampaignTotals, destination: AdDestinationKey): CampaignReport {
  const costPerConversionCents = t.conversions > 0 ? Math.round(t.spendCents / t.conversions) : null;
  const roas = t.spendCents > 0 && destination === 'STOREFRONT' ? Math.round((t.conversionValueCents / t.spendCents) * 100) / 100 : null;
  return { ...t, costPerConversionCents, roas, comment: reportComment(t, destination, roas) };
}

function reportComment(t: CampaignTotals, destination: AdDestinationKey, roas: number | null): string {
  if (t.spendCents === 0) return 'Seu anúncio ainda não começou a rodar.';
  if (destination === 'WHATSAPP') {
    if (t.conversions === 0) return 'Ainda não chegaram conversas pelo anúncio. Os primeiros dias servem para a Meta aprender quem se interessa.';
    return `Cada conversa iniciada custou ${formatBRL(Math.round(t.spendCents / t.conversions))}.`;
  }
  if (!roas) return 'Ainda não houve vendas pelo anúncio. Os primeiros dias servem para a Meta aprender quem compra.';
  const each = `cada R$ 1 em anúncio trouxe ${formatBRL(Math.round(roas * 100))} em vendas`;
  if (roas >= 3) return `Excelente: ${each}.`;
  if (roas >= 1) return `Bom: ${each}.`;
  return `Atenção: ${each}. Vale revisar os produtos ou o público.`;
}

// ---------- Conversões pelo servidor (14.6) ----------
/** Mesmo `event_id` que o pixel do navegador usa, para a Meta deduplicar. */
export const purchaseEventId = (orderId: string) => `order:${orderId}`;
